/**
 * API-Client: einzige Stelle für HTTP-Aufrufe.
 *  - Cookies (httpOnly) für die Sitzung, CSRF-Header bei schreibenden Anfragen
 *  - Automatische Token-Erneuerung bei 401 (einmalig, parallel-sicher)
 *  - Einheitliche Fehlerobjekte mit deutscher Meldung
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
  /** Feldfehler aus zod-Validierung: { feld: "Meldung" } */
  get fieldErrors(): Record<string, string> {
    const d = this.details as { fields?: Record<string, string> } | undefined;
    return d?.fields ?? {};
  }
}

type Query = Record<string, string | number | boolean | null | undefined | string[]>;

function qs(query?: Query): string {
  if (!query) return '';
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      if (v.length) p.set(k, v.join(','));
    } else p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

function csrfToken(): string {
  const m = document.cookie.match(/(?:^|;\s*)mv_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : '';
}

let refreshing: Promise<boolean> | null = null;
const unauthorizedListeners = new Set<() => void>();

export function onUnauthorized(fn: () => void) {
  unauthorizedListeners.add(fn);
  return () => {
    unauthorizedListeners.delete(fn);
  };
}

async function refreshSession(): Promise<boolean> {
  refreshing ??= fetch('/api/auth/refresh', { method: 'POST', credentials: 'include', headers: { 'X-CSRF-Token': csrfToken() } })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => {
      setTimeout(() => (refreshing = null), 0);
    });
  return refreshing;
}

async function request<T>(method: string, path: string, body?: unknown, opts: { query?: Query; raw?: boolean; retry?: boolean } = {}): Promise<T> {
  const isForm = body instanceof FormData;
  const headers: Record<string, string> = {};
  if (!isForm && body !== undefined) headers['Content-Type'] = 'application/json';
  if (method !== 'GET') headers['X-CSRF-Token'] = csrfToken();
  const res = await fetch(`/api${path}${qs(opts.query)}`, {
    method,
    credentials: 'include',
    headers,
    body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
  });
  if (res.status === 401 && opts.retry !== false && !path.startsWith('/auth/login') && !path.startsWith('/auth/refresh')) {
    if (await refreshSession()) return request<T>(method, path, body, { ...opts, retry: false });
    unauthorizedListeners.forEach((fn) => fn());
  }
  if (!res.ok) {
    let payload: { error?: { code?: string; message?: string; details?: unknown } } = {};
    try {
      payload = await res.json();
    } catch {
      /* keine JSON-Antwort */
    }
    throw new ApiError(
      res.status,
      payload.error?.code ?? 'FEHLER',
      payload.error?.message ?? (res.status >= 500 ? 'Serverfehler. Bitte später erneut versuchen.' : 'Die Anfrage konnte nicht verarbeitet werden.'),
      payload.error?.details,
    );
  }
  if (opts.raw) return res as unknown as T;
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') ?? '';
  return (ct.includes('application/json') ? res.json() : res.text()) as Promise<T>;
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>('GET', path, undefined, { query }),
  post: <T>(path: string, body?: unknown, query?: Query) => request<T>('POST', path, body ?? {}, { query }),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body ?? {}),
  del: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
  /** Datei-Upload (multipart). Zusätzliche Felder vor der Datei anhängen. */
  upload: <T>(path: string, file: File, fields: Record<string, string | undefined | null> = {}) => {
    const fd = new FormData();
    for (const [k, v] of Object.entries(fields)) if (v !== undefined && v !== null) fd.append(k, v);
    fd.append('file', file);
    return request<T>('POST', path, fd);
  },
  /** Datei herunterladen (PDF/CSV/XLSX) – öffnet PDFs im neuen Tab, sonst Download. */
  download: async (path: string, query?: Query, fileName?: string) => {
    await saveResponse(await request<Response>('GET', path, undefined, { query, raw: true }), fileName);
  },
  /** Download per POST (z. B. mit Passwortbestätigung im Body) */
  downloadPost: async (path: string, body: unknown) => {
    await saveResponse(await request<Response>('POST', path, body, { raw: true }));
  },
  /** URL für <a href>/<img src> auf authentifizierte Dateien (Cookie-basiert) */
  url: (path: string, query?: Query) => `/api${path}${qs(query)}`,
};

async function saveResponse(res: Response, fileName?: string) {
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const cd = res.headers.get('content-disposition') ?? '';
  const name = fileName ?? decodeURIComponent(cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/)?.[1] ?? 'download');
  if (blob.type === 'application/pdf' && !fileName) {
    window.open(url, '_blank', 'noopener');
  } else {
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export interface Paged<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  pages: number;
}
